"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { destroyCookie, parseCookies, setCookie } from "nookies";
import { env } from "@/env";
import type { UserRole } from "@/types/script";

interface User {
  id: string;
  username: string;
  bio: string | null;
  nickname: string;
  avatar: string | null;
  role: UserRole;
  verified: boolean;
  restricted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UpdateUserDetails {
  username?: string;
  nickname?: string;
  avatar?: string | null;
  verified?: boolean;
  restricted?: boolean;
  role?: UserRole;
  bio?: string | null;
}

interface AuthContextData {
  user: User | null;
  accessToken: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(details: UpdateUserDetails): Promise<void>;
  updateUserDetails(details: UpdateUserDetails): void;
}

interface SignInCredentials {
  username: string;
  password: string;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AUTH_STORAGE_KEY = "auth_data";
const AuthContext = createContext<AuthContextData>({} as AuthContextData);

function setAuthData(token: string, username: string, userId: string) {
  try {
    setCookie(undefined, "auth.token", token, {
      maxAge: 60 * 60 * 24 * 30,
      path: "/",
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });
    setCookie(undefined, "auth.username", username);
    setCookie(undefined, "auth.userId", userId);
  } catch {
    // Fallback to localStorage
    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({ token, username, userId }),
    );
  }
}

function getAuthData() {
  try {
    const cookies = parseCookies();
    if (cookies["auth.token"]) {
      return {
        token: cookies["auth.token"],
        username: cookies["auth.username"],
        userId: cookies["auth.userId"],
      };
    }
    const data = localStorage.getItem(AUTH_STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

function clearAuthData() {
  try {
    destroyCookie(undefined, "auth.token");
    destroyCookie(undefined, "auth.username");
    destroyCookie(undefined, "auth.userId");
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    console.error("Failed to clear auth data");
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const authData = getAuthData();
    if (authData?.token && authData?.username) {
      validateToken(authData.token, authData.username, authData.userId!);
    } else {
      setLoading(false);
    }
  }, []);

  async function validateToken(
    token: string,
    username: string,
    userId: string,
  ) {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/user/my-favorites`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const userData = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/user/${username}`,
      ).then((res) => res.json());

      if (response.status === 429 || response.ok) {
        setUser({
          id: userId,
          username: userData.data.user.username,
          nickname: userData.data.user.nickname,
          avatar: userData.data.user.avatar,
          role: userData.data.user.role,
          verified: userData.data.user.verified,
          bio: userData.data.user.bio,
          restricted: userData.data.user.restricted,
          createdAt: userData.data.user.created_at,
          updatedAt: userData.data.user.updated_at,
        });
        setAccessToken(token);
      }
    } catch {
      clearAuthData();
    } finally {
      setLoading(false);
    }
  }

  async function signIn({ username, password }: SignInCredentials) {
    try {
      const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/auth/sign-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (response.status === 429) {
        throw new Error("Rate limit exceeded. Please try again later.");
      }

      const data = await response.json();

      if (!response.ok) {
        let errorDetails = data.message;
        if (data.errors) {
          errorDetails = Object.values(data.errors)
            .map((error: any) => `${error.param.msg}`)
            .join(", ");
        }
        throw new Error(errorDetails ?? "Failed to sign in");
      }

      const { token, userId } = data.data.session;

      setAuthData(token, username, userId);

      const userData = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/user/${username}`,
      ).then((res) => res.json());

      setUser({
        id: userId,
        username: userData.data.user.username,
        nickname: userData.data.user.nickname,
        avatar: userData.data.user.avatar,
        role: userData.data.user.role,
        verified: userData.data.user.verified,
        bio: userData.data.user.bio,
        restricted: userData.data.user.restricted,
        createdAt: userData.data.user.created_at,
        updatedAt: userData.data.user.updated_at,
      });
      setAccessToken(token);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }

  function signOut() {
    clearAuthData();
    setUser(null);
    setAccessToken(null);
  }

  async function updateUser(details: UpdateUserDetails) {
    if (!accessToken) throw new Error("No access token");

    try {
      const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/user/update`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(details),
      });

      if (!response.ok) {
        throw new Error("Failed to update user");
      }

      const data = await response.json();
      setUser((prev) => (prev ? { ...prev, ...data.user } : null));
    } catch (error) {
      throw error;
    }
  }

  function updateUserDetails(details: UpdateUserDetails) {
    setUser((prev) => (prev ? { ...prev, ...details } : null));
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        loading,
        isAuthenticated: !!user,
        signIn,
        signOut,
        updateUser,
        updateUserDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
